import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, DOCUMENT, inject } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';
import { BehaviorSubject, filter, finalize, Observable, of, Subscription, switchMap, take, tap } from 'rxjs';
import { ConsumerRedirectionConfig } from '../../models';
import { ConsumerPortalConfig, CONSUMER_PORTAL_APP_CONFIG } from '../../models/consumer-portal-config';
import { TranslateService } from '@ngx-translate/core';
import { AuthUiService } from '../../services/auth-ui.service';
import { NotificationService } from '../../services';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { Customer } from '../../models/consumer';
import { AuthKeys } from '../../enums/local-storage-keys.enum';
import { LoginSession } from '../../models/login-session.model';
import { Auth } from '../../models/auth.model';
import { ConfigurationService } from '../../services/configuration.service';
import { RedirectionUsers } from '../../models/redirection-users';
import { consumerPortalDefaults } from '../../../input-config/consumer-portal-config';
import { GenericError } from '../../models/error.model';
import { ErrorService } from '../../services/error.service';
import { ComponentsToLoad } from '../../enums/components-to-load.enum';
import { AppEventService } from '../../services/app-event.service';
import { OriginActionName } from '@por/shared/ui/cross-app';
import { VersionToggleService } from '../../services/version-toggle';
import { OverlayContainer } from '@angular/cdk/overlay';
import { PdfService } from '../../services/pdf.service';
import { DEFAULT_LOCALE } from '../../constants/default.const';
import { AppFacadeService } from '../../services/app-facade.service';
import { AppMediatorService } from '../../services/app-mediator.service';
import { Redirection } from '../../types/redirection.type';
import { NetworkService } from '../../services/network.service';
import { PathRoutingService } from '../../services/path-routing.service';
import { PorPayEvent } from '../../payment/models/paymentOutput';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
@Component({
    standalone: false,
    selector: 'por-consumer-portal',
    templateUrl: './customer-portal.component.html',
    styleUrls: ['./customer-portal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerPortalComponent implements OnInit, OnChanges, OnDestroy {
    private readonly auth = inject(AuthUiService);
    private readonly translateService = inject(TranslateService);
    private readonly appConfig = inject<ConsumerPortalConfig>(CONSUMER_PORTAL_APP_CONFIG);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly featureToggleService = inject(FeatureToggleService);
    readonly configService = inject(ConfigurationService);
    private readonly errorService = inject(ErrorService);
    private readonly appEventService = inject(AppEventService);
    readonly versionToggleService = inject(VersionToggleService);
    private readonly overlayContainer = inject(OverlayContainer);
    private readonly pdfService = inject(PdfService);
    private readonly networkService = inject(NetworkService);
    private readonly notificationService = inject(NotificationService);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly breakpointObserver = inject(BreakpointObserver);

    constructor() {
        const appConfig = this.appConfig;
        const overlayContainer = this.overlayContainer;

        // Initialize app event service
        this.appEventService.initialize();
        // Register event actions
        this.registerEventActions();
        this.auth.initAuth();
        this.configService.setConsumerPortalConfigurations(undefined, { ...consumerPortalDefaults, ...appConfig });
        overlayContainer.getContainerElement().classList.add('customer-portal-wrappper-cp');
    }

    @Input() config!: string;
    cpInput!: ConsumerPortalConfig;
    @Output()
    readonly errorOutput: EventEmitter<GenericError> = new EventEmitter<GenericError>();
    @Output() readonly crossAppPorPayEvent: EventEmitter<PorPayEvent> = new EventEmitter<PorPayEvent>();
    showloader: boolean | undefined = true;
    errorMessage?: string;
    alertMessage = '';
    quickLinkUrl = '';
    subscriptions: Subscription[] = [];
    orgId!: string;
    email: string | undefined;
    redirectionConfig: ConsumerRedirectionConfig | undefined;
    customerId!: string;
    featureService: FeatureToggleService = this.featureToggleService;
    customer$: Observable<Customer> = this.appFacadeService.getCustomer();
    componentRedirection$!: Observable<Redirection | null>;
    selectedLocale!: string;
    reInit = false;

    authUser!: LoginSession;
    contractStatusFilters!: string[];
    isHaveSecurePdf$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    securePdfUrl = '';

    async ngOnInit() {
        this.initializeCpInput();

        this.translateService.use(DEFAULT_LOCALE);

        // Subscribe to watch http error
        this.subscriptions.push(
            this.errorService.error$.subscribe(result => {
                if (result) {
                    this.errorMessage = result.message;
                    this.emitError(result);
                }
            })
        );

        this.subscriptions.push(
            this.translateService.onLangChange.subscribe(() => {
                this.reInit = false;
                setTimeout(() => {
                    this.reInit = true;
                    this.cdr.detectChanges(); // Manually trigger change detection
                });
            })
        );
        this.reInit = true;
        this.document.body.style.backgroundColor = 'var(--por-cp-gray1)';
        this.appFacadeService.getRedirectionSessionData();
        this.subscriptions.push(
            this.appFacadeService.getRedirectionSessionData().subscribe(session => {
                if (session) {
                    this.setLoginSession(session);
                }
            })
        );

        this.subscriptions.push(
            /**
             * 769px or less than 769px = mobile
             */
            this.breakpointObserver.observe(['(max-width: 779px)']).subscribe((result: BreakpointState) => {
                this.appFacadeService.setAppDeviceType(result.matches);
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        Object.entries(changes).forEach(([key, value]) => {
            if (key === 'config' && value !== undefined) {
                /**
                 * Set input config which have been provided through when embeded index.html
                 */
                let hasPaymentToken = false;
                if (this.config && !isEmpty(this.config)) {
                    const parsedConfig = JSON.parse(this.config) as ConsumerPortalConfig;
                    hasPaymentToken = !!parsedConfig.paymentToken;

                    // Payment token flow: Clear localStorage to ensure fresh initialization
                    // Prevents stale tokens from triggering login flow on browser refresh
                    if (hasPaymentToken) {
                        this.appMediatorService.localStorageService.removeItem(AuthKeys.User, false);
                    }

                    this.configService.setInputsConfig({
                        ...this.configService.inputs,
                        ...parsedConfig
                    });
                    this.configService.setConsumerPortalConfigurations(this.config, { ...consumerPortalDefaults, ...this.appConfig });
                }
                this.initializeCpInput();

                // Skip synchronous redirection check for payment token (async extraction handles it)
                if (!hasPaymentToken) {
                    this.setRedirectComponentSession();
                }
                return;
            }
        });
    }
    // Note camelCase : DB Model
    // eslint-disable-next-line @typescript-eslint/naming-convention
    setActiveComponent(data: { OrganizationId: string; Type: string }) {
        this.appFacadeService.startRedirection(ComponentsToLoad.Subdomain, data.OrganizationId, this.cpInput);
    }

    loadConsumerPortal($e: boolean) {
        this.appFacadeService.setLoading($e);
    }

    onLoginSuccess(authUser: Auth) {
        // Set InitialLogin to true to set refresh token timer behaviour
        this.setLoginSession({ ...authUser, initialLogin: true });

        // Dispatching Sync Payment to RMS
        this.appFacadeService.dispatchSyncPaymentToRMS();
    }

    private setLoginSession(authUser: LoginSession) {
        /**
         * When refresh has been done using browser
         */
        if (this.cpInput === undefined) {
            this.initializeCpInput();
        }
        if (authUser?.OrganizationId) {
            this.orgId = authUser?.OrganizationId;
        }
        this.email = authUser?.Email;
        this.authUser = authUser;
        this.redirectionConfig = {
            orgId: this.orgId,
            email: this.email,
            apiUrl: this.cpInput?.apiUrl ? this.cpInput?.apiUrl : (this.appConfig.apiUrl as string),
            routeTo: this.cpInput?.routeTo
        };

        /**
         * Update CustomerId and orgId from auth session
         */
        this.configService.setConsumerPortalConfigurations(undefined, {
            ...this.cpInput,
            organizationId: authUser?.OrganizationId ?? this.cpInput.organizationId,
            organizationType: authUser?.OrganizationType ?? this.cpInput.organizationType,
            customerId: this.cpInput?.customerId ?? this.customerId
        });

        // TODO: Not to save all input properties in localstorageservice
        const loggedInUserConfig = {
            ...this.cpInput,
            /* eslint-disable @typescript-eslint/naming-convention */
            /**
             * Note camelCase : DB Model
             */
            Auth: {
                accessToken: authUser.AccessToken ?? authUser.accessToken,
                RefreshToken: authUser.RefreshToken,
                initialLogin: false,
                customerId: this.cpInput?.customerId ?? authUser.customerId,
                OrganizationType: authUser.OrganizationType,
                OrganizationId: authUser.OrganizationId,
                Email: authUser.Email
            }
        };
        this.featureToggleService.setType(authUser.OrganizationType);
        // Always set InitialLogin to false in the localstorageservice
        this.appMediatorService.localStorageService.persistUser(AuthKeys.User, loggedInUserConfig);
        this.config = JSON.stringify(this.cpInput);
        // Note camelCase : DB Model
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.handleRefreshToken({ ...authUser, OrganizationId: loggedInUserConfig?.organizationId });
        this.appFacadeService.setAuthenticated(!isEmpty(authUser.accessToken));

        // Try open secure pdf if available
        if (authUser.customerId) {
            this.customerId = authUser.customerId;
        }
        this.appFacadeService.processRoute(this.cpInput, this.orgId);
    }

    logout() {
        this.appFacadeService.setRedirected(false);
        this.appFacadeService.setLoading(true);
        this.isHaveSecurePdf$.next(false);
        /**
         * Remove session storage on logout, so it would remove secure pdf url and reset customer id
         */
        this.appMediatorService.localStorageService.removeSessionItem(AuthKeys.User);
        this.customerId = '';
        this.configService.setConsumerPortalConfigurations(undefined, {
            ...this.cpInput,
            customerId: ''
        });

        /**
         * Clear alertMessage, so it wouldn't be visible on consumer login
         */
        this.alertMessage = '';
        this.auth.logout();
    }

    /**
     * This will subscribe and autorefresh token every 5 minutes
     * Refresh token is stored in localStorageService so it can be used anytime to check if user has one active
     */
    handleRefreshToken(authDataObj: LoginSession) {
        this.subscriptions.push(this.auth.activateRefreshAuthTimer(authDataObj).subscribe());
    }

    handleRedirection(contact: RedirectionUsers) {
        if (contact === undefined) {
            this.notificationService.error(this.translateService.instant('unauthorize'));
            this.logout();
            return;
        }
        this.subscriptions.push(
            this.isHaveSecurePdf$.subscribe((check: boolean) => {
                if (check && this.securePdfUrl && contact !== undefined) {
                    this.pdfService.openPdfinNewTab(this.securePdfUrl);
                }
            })
        );
        const user: ConsumerPortalConfig = this.appMediatorService.localStorageService.getCurrentUser as ConsumerPortalConfig;
        this.appMediatorService.localStorageService.persistUser(AuthKeys.User, {
            ...user,
            organizationType: user?.Auth?.OrganizationType,
            // Note camelCase : DB Model
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Auth: {
                ...user?.Auth,
                customerId: contact?.customerId,
                initialLogin: false // Always set InitialLogin to false in the localstorageservice
            }
        });

        this.customerId = contact?.customerId;

        if (!this.versionToggleService.isQuickLinkVersion() && !this.versionToggleService.isAdvanceVersion()) {
            /**
             * Load customer if redirected
             */
            this.appFacadeService.loadCustomerById(this.customerId);
        }
        /**
         * Update CustomerId
         */
        this.configService.setConsumerPortalConfigurations(undefined, {
            ...this.cpInput,
            customerId: this.customerId
        });
        this.appFacadeService.setRedirected(true);

        // Dispatching action to fetch permission
        this.appFacadeService.fetchAccountFeature(this.customerId);
    }

    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
        this.appEventService.destroy();
    }

    getCustomerId(customerId: string) {
        if (customerId) {
            this.customerId = customerId;
        }
    }

    openContractsWithFilter(filterString: string) {
        this.appFacadeService.setActiveTab(ComponentsToLoad.Contracts);
        this.contractStatusFilters = [filterString];
    }

    clearInitFilters() {
        this.contractStatusFilters = [];
    }

    // Emite error to the parent component using the errorOutput event
    private emitError(error: GenericError) {
        this.errorOutput.emit(error);
    }

    /**
     * This will register all the actions related to this component that are required to be performed on the event
     */
    private registerEventActions() {
        this.appEventService.registerActions({
            [OriginActionName[OriginActionName.LogOff]]: () => {
                return this.logout();
            }
        });
    }

    /**
     * This function will only executes when a configuration is set
     * when consumer portal is embeded in the client's website as web component
     */
    private setRedirectComponentSession() {
        // Detect path-routed URLs FIRST — public access, no auth required.
        // Uses startPublicRedirection to resolve orgId and fetch org config (HiddenFeatures)
        // before the component renders, so feature toggles have correct data.
        if (PathRoutingService.isAssetTag) {
            this.appFacadeService.startPublicRedirection(ComponentsToLoad.AssetTag, this.cpInput);
            return; // Skip all auth logic — public path
        }

        if (!this.configService.inputs?.organizationId?.toString()?.length) {
            this.appFacadeService.setLoading(false);
            if (this.versionToggleService.isConsumerQuickLinkVersion()) {
                this.appFacadeService.startRedirection(ComponentsToLoad.ConsumerPortal, (this.orgId as string) || '', this.cpInput);
            }
            if (this.versionToggleService.isQuickLinkVersion()) {
                this.appFacadeService.startRedirection(ComponentsToLoad.QuickLink, (this.orgId as string) || '', this.cpInput);
                return;
            }
            this.appFacadeService.startRedirection(ComponentsToLoad.Subdomain, (this.orgId as string) || '', this.cpInput);
        } else {
            if ((this.cpInput?.routeTo?.route as ComponentsToLoad) === ComponentsToLoad.Payment) {
                this.appFacadeService.startRedirection(ComponentsToLoad.Payment, (this.cpInput.organizationId as string) || '', this.cpInput);
                // Payment token flow - skip setLoginSession() to avoid customer/find and refresh token calls
                return;
            } else {
                this.appFacadeService.startRedirection(ComponentsToLoad.ConsumerPortal, this.configService.inputs.organizationId || '', this.cpInput);
            }
        }

        if (this.auth?.user) {
            this.setLoginSession(this.auth.user as LoginSession);
        } else if (this.configService.inputs?.Auth) {
            // Note camelCase : DB Model
            // eslint-disable-next-line @typescript-eslint/naming-convention
            this.setLoginSession({ ...this.configService.inputs?.Auth, OrganizationId: this.configService.inputs?.organizationId, initialLogin: false } as LoginSession);
        }
    }

    returnToLoginFromQuickLink($message: string) {
        this.appFacadeService.setRedirected(false);
        this.appFacadeService.setLoading(true);
        this.alertMessage = $message;
        this.auth.returnToLogin();
    }

    private initializeCpInput() {
        this.subscriptions.push(
            this.configService.cpInputs$.subscribe(inputs => {
                this.cpInput = inputs as unknown as ConsumerPortalConfig;
            })
        );
    }

    returnQuickLinkFromLogin($url: string) {
        this.quickLinkUrl = $url;
    }

    handlePoyPaymentCrossAppEvent(event: PorPayEvent) {
        this.crossAppPorPayEvent.emit(event);
    }

    /**
     * If have in state then render from state else fetch it
     */
    viewContacts() {
        this.subscriptions.push(
            this.appFacadeService
                .getCustomerContacts()
                .pipe(
                    take(1), // Get current store value once
                    tap(data => {
                        if (!data || data.length === 0) {
                            this.appFacadeService.setLoading(true);
                            this.appFacadeService.loadCustomerContacts(this.customerId);
                        }
                    }),
                    switchMap(data => {
                        // If data is present, we already have it.
                        return data && data.length > 0
                            ? of(data)
                            : this.appFacadeService.getCustomerContacts().pipe(
                                  filter(d => !!d && d.length > 0),
                                  take(1)
                              );
                    }),
                    tap(() => {
                        this.appFacadeService.showManageUserDialogDisplayed(true);
                    }),
                    finalize(() => {
                        this.appFacadeService.setLoading(false);
                    })
                )
                .subscribe()
        );
    }

    closeContact() {
        this.appFacadeService.showManageUserDialogDisplayed(false);
    }
}
